import { useState, useEffect, useCallback } from "react";
import {
  TrendingUp,
  Users,
  Award,
  DollarSign,
  PieChart as PieIcon,
  BarChart3,
  Sparkles,
  ShoppingBag,
  Layers,
  ArrowUpRight,
  Info,
  Package,
  CreditCard,
} from "lucide-react";
import DatePicker from "@/components/ui/DatePicker";
import { motion } from "framer-motion";
import api from "../lib/api";
import { monthIST } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";

const COLORS = ["#0F172A", "#D97706", "#10B981", "#6366F1", "#EC4899", "#8B5CF6"];

export default function SalesAnalyticsPage() {
  const [salesBreakdown, setSalesBreakdown] = useState([]);
  const [consumption, setConsumption] = useState(null);
  const [collection, setCollection] = useState(null);
  const [tenderBreakdown, setTenderBreakdown] = useState([]);
  const [tenderSummary, setTenderSummary] = useState(null);
  const [staffPerf, setStaffPerf] = useState([]);
  // Monthly incentive from /incentives/monthly (owner, 27 Oct 2026: the
  // monthly figure must NOT include product or card incentive — those are
  // paid out on the daily incentive, so double-counting them here was wrong).
  const [monthlyInc, setMonthlyInc] = useState([]);
  const [month, setMonth] = useState(monthIST());
  const [pageLoading, setPageLoading] = useState(true);

  const fetchData = useCallback(async (silent = false) => {
    // `silent` keeps the full-page skeleton off for the background poll — the
    // numbers swap in underneath instead of blanking the report every 30s.
    if (!silent) setPageLoading(true);
    try {
      // `/analytics/sales` filters on date_from/date_to, not on `month` --
      // without them the KPIs and the donut always showed all-time figures
      // while only the staff section followed the selector (owner report,
      // Oct 2026). Derive the month's range here; backend untouched.
      let range = "";
      if (/^\d{4}-\d{2}$/.test(month || "")) {
        const [y, m] = month.split("-").map(Number);
        const last = new Date(y, m, 0).getDate();
        range = `?date_from=${month}-01&date_to=${month}-${String(last).padStart(2, "0")}`;
      }
      const [bRes, sRes, iRes] = await Promise.all([
        api.get(`/analytics/sales${range}`),
        api.get(`/analytics/staff-performance?month=${month}`),
        // Monthly incentive components — needed to drop product & card.
        api.get(`/incentives/monthly?month=${month}`),
      ]);
      setSalesBreakdown(bRes.data?.breakdown || []);
      setConsumption(bRes.data?.consumption || null);
      setCollection(bRes.data?.collection || null);
      setTenderBreakdown(bRes.data?.tender_breakdown || []);
      setTenderSummary(bRes.data?.tender_summary || null);
      setStaffPerf(sRes.data?.staff || []);
      setMonthlyInc(iRes.data?.rows || []);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setPageLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(() => fetchData(true), 30000);

  const totalRevenue = salesBreakdown.reduce((sum, item) => sum + (item.revenue || 0), 0);
  const totalTxns = salesBreakdown.reduce((sum, item) => sum + (item.count || 0), 0);
  const totalDiscounts = salesBreakdown.reduce((sum, item) => sum + (item.total_discount || 0), 0);

  const productData = salesBreakdown.find((x) => x.type?.toLowerCase() === "product") || { revenue: 0, count: 0 };
  const retailRatio = totalRevenue ? ((productData.revenue / totalRevenue) * 100).toFixed(1) : 0;

  // Chart Data Preparation
  const pieChartData = salesBreakdown.map((item) => ({
    name: item.type,
    value: item.revenue || 0,
    count: item.count,
    discounts: item.total_discount,
  }));

  const sortedStaff = [...staffPerf].sort((a, b) => {
    const combinedA = (a.service_revenue || 0) + (a.retail_revenue || 0);
    const combinedB = (b.service_revenue || 0) + (b.retail_revenue || 0);
    return combinedB - combinedA;
  });

  const totalStaffService = sortedStaff.reduce((sum, s) => sum + (s.service_revenue || 0), 0);
  const totalStaffRetail = sortedStaff.reduce((sum, s) => sum + (s.retail_revenue || 0), 0);
  const totalStaffCombined = totalStaffService + totalStaffRetail;
  const totalStaffIncentives = sortedStaff.reduce((sum, s) => sum + (s.incentive || 0), 0);
  const totalServicesCount = sortedStaff.reduce((sum, s) => sum + (s.service_count || 0), 0);
  const totalProductsCount = sortedStaff.reduce((sum, s) => sum + (s.retail_count || 0), 0);

  // Monthly incentive, keyed by staff name — POS names and the roster names
  // differ only in case/spacing, so they are folded first.
  //   Monthly = efficiency bonus + retail commission.
  // EXCLUDED: product incentive and prepaid-card bonus — the owner pays those
  // on the daily incentive, so adding them here would count them twice.
  const normName = (n) => String(n || "").trim().toLowerCase().replace(/\s+/g, " ");
  const monthlyIncentive = {};
  (monthlyInc || []).forEach((r) => {
    const v = Math.round(
      ((Number(r.efficiency_bonus) || 0) + (Number(r.retail_commission) || 0)) * 100
    ) / 100;
    monthlyIncentive[normName(r.staff_name)] = v;
    monthlyIncentive[normName(r.staff_name).replace(/\s/g, "")] = v;
  });
  const monthlyIncentiveOf = (name) =>
    monthlyIncentive[normName(name)] ??
    monthlyIncentive[normName(name).replace(/\s/g, "")] ??
    0;
  const totalMonthlyIncentive = sortedStaff.reduce((sum, s) => sum + monthlyIncentiveOf(s.name), 0);

  const staffBarChartData = sortedStaff.slice(0, 8).map((s) => ({
    name: s.name,
    fullName: s.name,
    Service: Math.round(s.service_revenue || 0),
    Retail: Math.round(s.retail_revenue || 0),
    Combined: Math.round((s.service_revenue || 0) + (s.retail_revenue || 0)),
    Incentive: Math.round(s.incentive || 0),
  }));

  if (pageLoading) return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8 animate-pulse">
      <div className="h-10 bg-slate-200 rounded w-1/3 mb-4"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-32 bg-slate-200 rounded-2xl"></div>)}
      </div>
      <div className="h-96 bg-slate-200 rounded-2xl w-full"></div>
    </div>
  );

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 py-2 border-b border-[#E6DACD]/60 pb-6"
      >
        <div>
          <div className="lss-overline text-amber-800 flex items-center gap-1.5 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
            EXECUTIVE REVENUE INTELLIGENCE
          </div>
          <h1 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-amber-600 shrink-0" /> Sales Analytics & Insights
          </h1>
          <p className="text-slate-600 font-medium text-sm sm:text-base mt-2 max-w-xl leading-relaxed">
            Real-time sales distribution, staff contribution rankings, and service-to-retail conversion ratios.
          </p>
        </div>

        <div className="flex items-center gap-4 self-end lg:self-center shrink-0">
          <DatePicker type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          {/* Framed Aesthetic Salon Image Card */}
          <div className="hidden xl:block relative neu-card p-2 h-36 w-64 overflow-hidden shrink-0">
            <img src="/assets/salon_plants.png" alt="Salon Aesthetic" className="w-full h-full object-cover rounded-2xl" />
          </div>
        </div>
      </motion.div>

      {/* Top Level Metric Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <motion.div
          whileHover={{ y: -3 }}
          className="p-5 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white rounded-2xl border border-amber-500/40 shadow-xl relative overflow-hidden flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between text-slate-300">
              <span
                className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5 cursor-help"
                title="Calculated as sum of Net Price across all POS invoices"
              >
                Total Net Sales
                <Info className="w-3.5 h-3.5 text-amber-400/80 shrink-0" />
              </span>
              <DollarSign className="w-5 h-5 text-amber-400" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold mt-3 tabular tracking-tight text-white">
              ₹{totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="mt-2.5 flex items-center gap-2 text-xs font-medium text-slate-400">
              <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/30 flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3" /> Live POS
              </span>
              <span>{totalTxns} invoices</span>
            </div>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-800/80 text-[10px] text-slate-400 flex items-center justify-between">
            <span className="text-slate-400 font-medium">Calculated as:</span>
            <span className="text-amber-300 font-semibold tracking-tight">Sum of Net Price</span>
          </div>
        </motion.div>

        {/* Taxable KPI Card with real value formula (owner, 27 Oct 2026: the
            old "Total Collection" heading was the wrong label — the figure is
            the taxable value, which IS Total Collection − Tax) */}
        <motion.div
          whileHover={{ y: -3 }}
          className="lss-card p-5 border-amber-300/80 bg-amber-50/25 flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between text-slate-600">
              <span
                className="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5 cursor-help"
                title="Taxable = Total Collection − Tax"
              >
                Taxable
                <Info className="w-3.5 h-3.5 text-amber-700/80 shrink-0" />
              </span>
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold mt-3 text-slate-900 tabular tracking-tight">
              ₹{(collection?.net_collection || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="mt-2 text-xs font-medium text-slate-600 truncate">
              Total Collection: ₹{(collection?.gross_collection || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} · Tax: ₹{(collection?.tax || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="mt-2.5 pt-2 border-t border-amber-200/80 text-[10px] text-slate-600 flex flex-col gap-0.5">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 font-medium">Calculation:</span>
              <span className="text-slate-700 font-bold">Total Collection − Tax</span>
            </div>
            <div className="text-amber-900 font-black tracking-tight text-[11px] tabular text-right">
              {collection?.formula_str || "₹0 − ₹0 = ₹0"}
            </div>
          </div>
        </motion.div>

        {/* Consumption KPI Card */}
        <motion.div whileHover={{ y: -3 }} className="lss-card p-5 border-rose-200/80 bg-rose-50/20 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-slate-600">
              <span
                className="text-xs font-bold uppercase tracking-wider text-rose-800 flex items-center gap-1.5 cursor-help"
                title="Total Stock Consumption Valuation (Technical + Retail) and % with respect to Service Revenue"
              >
                Stock Consumption
                <Info className="w-3.5 h-3.5 text-rose-700/80 shrink-0" />
              </span>
              <Package className="w-5 h-5 text-rose-600" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold mt-3 text-slate-900 tabular flex items-baseline gap-1.5 flex-wrap">
              <span>₹{(consumption?.total_valuation || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className="text-xs font-black px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-300">
                {consumption?.total_pct_of_service || 0}% of Svc
              </span>
            </div>
            <div className="mt-2 text-xs font-semibold text-slate-600">
              {consumption?.total_units?.toLocaleString() || 0} total units consumed
            </div>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 text-[11px] text-slate-700 space-y-0.5">
            <div className="flex justify-between">
              <span>Technical:</span>
              <b className="text-slate-900 tabular">₹{(consumption?.technical_valuation || 0).toLocaleString()} ({consumption?.technical_pct_of_service || 0}%)</b>
            </div>
            <div className="flex justify-between">
              <span>Retail:</span>
              <b className="text-slate-900 tabular">₹{(consumption?.retail_valuation || 0).toLocaleString()} ({consumption?.retail_pct_of_service || 0}%)</b>
            </div>
          </div>
        </motion.div>

        <motion.div whileHover={{ y: -3 }} className="lss-card p-5 border-slate-200 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-slate-600">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Retail Share</span>
              <ShoppingBag className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold mt-3 text-slate-900 tabular flex items-baseline gap-2">
              <span>{retailRatio}%</span>
              <span className="text-xs font-semibold text-emerald-700">₹{productData.revenue?.toLocaleString() || 0}</span>
            </div>
            <div className="mt-2 text-xs font-medium text-slate-600">
              {productData.count || 0} retail product units sold
            </div>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-100 text-[10px] text-slate-500 font-medium">
            Retail conversion share of net sales
          </div>
        </motion.div>

        <motion.div whileHover={{ y: -3 }} className="lss-card p-5 border-slate-200 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-slate-600">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Discounts</span>
              <Layers className="w-5 h-5 text-rose-600" />
            </div>
            <div className="text-2xl sm:text-3xl font-extrabold mt-3 text-rose-900 tabular">
              ₹{totalDiscounts.toLocaleString()}
            </div>
            <div className="mt-2 text-xs font-medium text-slate-600">
              Discounts granted on POS bills
            </div>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-100 text-[10px] text-slate-500 font-medium">
            Membership, manager & offer discounts
          </div>
        </motion.div>
      </div>

      {/* Visual Analytics Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Category Revenue Distribution Donut Chart */}
        <div className="lg:col-span-5 lss-card p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="lss-overline text-amber-900">Revenue Breakdown</span>
              <PieIcon className="w-4 h-4 text-amber-600" />
            </div>
            <h3 className="text-xl font-extrabold text-slate-900">Sales Category Share</h3>
            <p className="text-xs font-medium text-slate-600 mt-1">
              Visual proportion of Service vs Product vs Advance by Net Price
            </p>
          </div>

          <div className="h-64 my-4 relative">
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(val) => [`₹${Number(val).toLocaleString()}`, "Revenue"]}
                  // The hover box used to be painted under the chart slices —
                  // it now sits above the SVG and carries its own light card.
                  wrapperStyle={{ zIndex: 1000, pointerEvents: "none" }}
                  labelStyle={{ color: "#0F172A" }}
                  itemStyle={{ color: "#0F172A", fontWeight: 700 }}
                  contentStyle={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #CBD5E1",
                    borderRadius: "10px",
                    boxShadow: "0 8px 20px rgba(15,23,42,0.18)",
                    color: "#0F172A",
                    fontSize: "12px",
                    fontWeight: 700,
                    padding: "8px 10px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: "12px", fontWeight: "600" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-2 pt-3 border-t border-slate-100">
            {salesBreakdown.map((item, idx) => {
              const pct = totalRevenue ? ((item.revenue / totalRevenue) * 100).toFixed(1) : 0;
              return (
                <div key={item.type} className="flex items-center justify-between text-xs font-medium">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                    <span className="font-bold text-slate-800">{item.type}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-extrabold text-slate-950">₹{item.revenue?.toLocaleString()}</span>
                    <span className="text-slate-600 font-bold tabular w-12 text-right">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Staff Sales Performance Bar Chart */}
        <div className="lg:col-span-7 lss-card p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="lss-overline text-indigo-900">Staff Contribution</span>
              <BarChart3 className="w-4 h-4 text-indigo-600" />
            </div>
            <h3 className="text-xl font-extrabold text-slate-900">Top Stylist & Staff Revenue Chart ({month})</h3>
            <p className="text-xs font-medium text-slate-600 mt-1">
              Comparison of Service Revenue vs Retail Product Sales and Incentives Given
            </p>
          </div>

          <div className="h-80 my-4">
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
              <BarChart data={staffBarChartData} margin={{ top: 10, right: 10, left: -10, bottom: 25 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis
                  dataKey="fullName"
                  tick={{ fontSize: 10, fontWeight: 700, fill: "#475569" }}
                  interval={0}
                  angle={-15}
                  textAnchor="end"
                  height={50}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 11, fontWeight: 600, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v / 1000}k`} />
                <Tooltip
                  formatter={(val, name) => [`₹${Number(val).toLocaleString()}`, name]}
                  labelFormatter={(label) => `Staff: ${label}`}
                  // Same visibility fix as the donut: lift the box above the
                  // bars and give it light card colours so the labels read.
                  wrapperStyle={{ zIndex: 1000, pointerEvents: "none" }}
                  labelStyle={{ color: "#0F172A" }}
                  itemStyle={{ color: "#0F172A", fontWeight: 700 }}
                  cursor={{ fill: "rgba(15,23,42,0.06)" }}
                  contentStyle={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #CBD5E1",
                    borderRadius: "10px",
                    boxShadow: "0 8px 20px rgba(15,23,42,0.18)",
                    color: "#0F172A",
                    fontSize: "12px",
                    fontWeight: 700,
                    padding: "8px 10px",
                  }}
                />
                <Legend verticalAlign="top" align="right" height={36} wrapperStyle={{ fontSize: "12px", fontWeight: "700" }} />
                <Bar dataKey="Service" fill="#0F172A" radius={[4, 4, 0, 0]} name="Service Revenue" />
                <Bar dataKey="Retail" fill="#D97706" radius={[4, 4, 0, 0]} name="Retail Revenue" />
                <Bar dataKey="Incentive" fill="#10B981" radius={[4, 4, 0, 0]} name="Incentive Given" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="p-3 bg-amber-50/60 border border-amber-200/70 rounded-xl text-xs font-semibold text-amber-950 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-700 shrink-0" />
              <span>Top Revenue Contributor: <strong className="text-amber-900 uppercase font-black">{sortedStaff[0]?.name || "N/A"}</strong></span>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <span>Revenue: <strong className="text-emerald-800 tabular font-extrabold">₹{((sortedStaff[0]?.service_revenue || 0) + (sortedStaff[0]?.retail_revenue || 0)).toLocaleString()}</strong></span>
              <span>Total Incentive Given: <strong className="text-amber-950 tabular font-black">₹{(sortedStaff[0]?.incentive || 0).toLocaleString()}</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* Tender / Payment Modes Collection Breakdown Table */}
      <div className="lss-card p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200 pb-4">
          <div>
            <div className="lss-overline text-amber-900">Tender Reconciliation</div>
            <h3 className="text-2xl font-extrabold text-slate-900 mt-0.5 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-amber-600" /> Payment & Tender Modes Collection ({month})
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <span className="text-xs font-semibold text-slate-600">
                Formula: <span className="text-amber-900 font-bold">Net Price = Cash + Gift Card + Other + Paid from Advance + Value Card − Tax Price</span>
              </span>
              {tenderSummary?.others_formula_str && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-bold bg-sky-50 text-sky-900 border border-sky-200">
                  <span className="text-sky-700 font-semibold">Digital (UPI/Cards):</span>
                  <span className="font-mono">{tenderSummary.others_formula_str}</span>
                </span>
              )}
            </div>
          </div>
          {/* Owner, 27 Oct 2026: the three summary chips that sat here
              (Gross Tenders / Less Tax / Net Price) are removed — the same
              three figures are already in the totals rows of the table. */}
        </div>

        <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-xs">
          <table className="lss-table text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-700">
                <th className="py-3 px-4 text-left">Payment / Tender Mode</th>
                <th className="py-3 px-3 text-center w-28">Invoices / Txns</th>
                <th className="py-3 px-4 text-right">Tender Amount (₹)</th>
                <th className="py-3 px-3 text-right w-24">Share (%)</th>
                <th className="py-3 px-4 text-left">Description / Details</th>
              </tr>
            </thead>
            <tbody>
              {tenderBreakdown.map((t) => (
                <tr key={t.mode} className="hover:bg-slate-50/80 transition">
                  <td className="py-3.5 px-4 font-bold text-slate-900 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-600"></span>
                    {t.mode}
                  </td>
                  <td className="py-3.5 px-3 text-center text-slate-700 font-bold tabular">
                    {t.count || 0}
                  </td>
                  <td className="py-3.5 px-4 text-right font-extrabold text-slate-950 text-sm tabular">
                    ₹{(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="py-3.5 px-3 text-right font-black text-amber-900 tabular bg-amber-50/40">
                    {t.pct || 0}%
                  </td>
                  <td className="py-3.5 px-4 text-slate-600 font-medium">
                    {t.notes}
                  </td>
                </tr>
              ))}
              {tenderBreakdown.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-slate-500 font-medium">
                    No tender mode collection records found for {month}.
                  </td>
                </tr>
              )}
            </tbody>
            {tenderBreakdown.length > 0 && (
              <tfoot>
                <tr className="bg-slate-100 font-extrabold text-slate-900 border-t-2 border-slate-300">
                  <td className="py-3 px-4">Total (Cash + VC + Other + Advance + Gift)</td>
                  <td className="py-3 px-3 text-center tabular">
                    {totalTxns}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-950 tabular text-sm">
                    ₹{(tenderSummary?.gross_tenders || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="py-3 px-3 text-right text-amber-950 font-black tabular bg-amber-100/80">
                    100.0%
                  </td>
                  <td className="py-3 px-4 text-slate-600 text-[11px] font-medium">
                    Sum of all settlement tenders before tax deduction
                  </td>
                </tr>
                <tr className="bg-rose-50/50 font-bold text-rose-900 border-t border-rose-200">
                  <td className="py-2.5 px-4 text-rose-900">Less: Tax Price (GST)</td>
                  <td className="py-2.5 px-3 text-center text-rose-700 tabular">—</td>
                  <td className="py-2.5 px-4 text-right text-rose-800 tabular text-sm font-extrabold">
                    −₹{(tenderSummary?.tax || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="py-2.5 px-3 text-right text-rose-800 tabular font-bold">
                    {tenderSummary?.gross_tenders ? ((tenderSummary.tax / tenderSummary.gross_tenders) * 100).toFixed(1) : 0}%
                  </td>
                  <td className="py-2.5 px-4 text-rose-700 text-[11px] font-medium">
                    Total GST deducted from billed tenders
                  </td>
                </tr>
                <tr className="bg-emerald-50/90 font-black text-emerald-950 border-t-2 border-emerald-400">
                  <td className="py-3.5 px-4 text-sm text-emerald-950 flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-600"></span>
                    Net Price (Sum = Total − Tax Price)
                  </td>
                  <td className="py-3.5 px-3 text-center text-emerald-900 tabular font-extrabold">
                    {totalTxns}
                  </td>
                  <td className="py-3.5 px-4 text-right text-emerald-950 tabular text-base font-black">
                    ₹{(tenderSummary?.net_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="py-3.5 px-3 text-right text-emerald-950 font-black tabular bg-emerald-100/90">
                    {tenderSummary?.gross_tenders ? ((tenderSummary.net_price / tenderSummary.gross_tenders) * 100).toFixed(1) : 0}%
                  </td>
                  <td className="py-3.5 px-4 text-emerald-900 text-xs font-bold">
                    {(tenderSummary?.formula_str || "Net Price = Total − Tax Price").replace(/Gross Tenders/g, "Total")}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Staff Leaderboard Table */}
      <div className="lss-card p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200 pb-4">
          <div>
            <div className="lss-overline text-emerald-900">Leaderboard Rankings</div>
            <h3 className="text-2xl font-extrabold text-slate-900 mt-0.5 flex items-center gap-2">
              <Users className="w-6 h-6 text-emerald-600" /> Staff Driven Revenue Performance ({month})
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-extrabold text-amber-950 bg-amber-100 border border-amber-300 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-xs">
              <span>Total Incentives Given:</span>
              <span className="text-amber-900 font-black">₹{totalStaffIncentives.toLocaleString()}</span>
            </span>
            <span className="text-xs font-extrabold text-slate-500 bg-slate-100 px-3 py-1 rounded-full self-start sm:self-auto">
              {sortedStaff.length} active staff members
            </span>
          </div>
        </div>

        <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-xs">
          <table className="lss-table text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-700">
                <th className="py-3 px-3 text-left w-12">Rank</th>
                <th className="py-3 px-3 text-left">Staff Member</th>
                <th className="py-3 px-3 text-right">Service Revenue</th>
                <th className="py-3 px-2 text-center">Services</th>
                <th className="py-3 px-3 text-right">Retail Revenue</th>
                <th className="py-3 px-2 text-center">Products</th>
                <th className="py-3 px-3 text-right">Combined Revenue</th>
                <th className="py-3 px-4 text-right font-black text-amber-950 bg-amber-100/70 border-l border-amber-300">
                  Total Incentive Given
                </th>
                <th
                  className="py-3 px-4 text-right font-black text-emerald-950 bg-emerald-100/70 cursor-help"
                  title="Efficiency bonus + retail commission for the month. Product and card incentive are NOT included — those are paid on the daily incentive."
                >
                  Monthly Incentive
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedStaff.map((s, idx) => {
                const combined = (s.service_revenue || 0) + (s.retail_revenue || 0);
                const rankBadge =
                  idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`;
                return (
                  <tr key={s.name} className="hover:bg-slate-50 transition">
                    <td className="py-3 px-3 font-bold text-slate-800 text-center text-sm">{rankBadge}</td>
                    <td className="py-3 px-3 font-bold text-slate-900 text-sm">{s.name}</td>
                    <td className="py-3 px-3 text-right font-bold text-emerald-700 tabular">
                      ₹{s.service_revenue?.toLocaleString()}
                    </td>
                    <td className="py-3 px-2 text-center text-slate-600 font-semibold tabular">
                      {s.service_count}
                    </td>
                    <td className="py-3 px-3 text-right font-bold text-indigo-700 tabular">
                      ₹{s.retail_revenue?.toLocaleString()}
                    </td>
                    <td className="py-3 px-2 text-center text-slate-600 font-semibold tabular">
                      {s.retail_count}
                    </td>
                    <td className="py-3 px-3 text-right font-extrabold text-slate-950 text-sm tabular bg-slate-50/50">
                      ₹{combined.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right font-black text-amber-900 text-sm tabular bg-amber-50/60 border-l border-amber-200">
                      ₹{(s.incentive || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right font-black text-emerald-800 text-sm tabular bg-emerald-50/60">
                      ₹{monthlyIncentiveOf(s.name).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
              {sortedStaff.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-slate-500 font-medium">
                    No staff sales performance records found for {month}.
                  </td>
                </tr>
              )}
            </tbody>
            {sortedStaff.length > 0 && (
              <tfoot>
                <tr className="bg-slate-100 font-extrabold text-slate-900 border-t-2 border-slate-300">
                  <td className="py-3 px-3" colSpan={2}>Total ({sortedStaff.length} Staff)</td>
                  <td className="py-3 px-3 text-right text-emerald-800 tabular">₹{totalStaffService.toLocaleString()}</td>
                  <td className="py-3 px-2 text-center tabular">{totalServicesCount}</td>
                  <td className="py-3 px-3 text-right text-indigo-800 tabular">₹{totalStaffRetail.toLocaleString()}</td>
                  <td className="py-3 px-2 text-center tabular">{totalProductsCount}</td>
                  <td className="py-3 px-3 text-right text-slate-950 tabular">₹{totalStaffCombined.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-amber-950 font-black tabular bg-amber-100/90 border-l border-amber-300 text-sm">
                    ₹{totalStaffIncentives.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-emerald-950 font-black tabular bg-emerald-100/90 text-sm">
                    ₹{totalMonthlyIncentive.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
